import { FC, useState, useMemo, useEffect } from 'react'
import {
  Box,
  Checkbox,
  IconButton,
  Menu,
  MenuItem,
  Typography,
  CircularProgress,
} from '@mui/material'
import MoreVertIcon from '@mui/icons-material/MoreVert'
import { MobileListItemPreview } from '../MobileListItemPreview'
import { useMobileListItems } from '@mobileHooks'
import { useExecuteCommandMutation } from '@redux/api/command.api'
import { getCommandRequestData } from '@helpers'

interface EmbeddedListElementPreviewProps {
  element: FormElement
  commandResults: Record<string, any[]>
  selectedIds: string[]
  onCheckboxChange: (itemId: string, checked: boolean) => void
  onContextMenuAction: (action: ContextMenuAction, item: any) => void
}

const normalizeCommandRecords = (result: any): any[] => {
  if (result === undefined || result === null) {
    return []
  }

  if (Array.isArray(result)) {
    if (result.length > 0 && typeof result[0] === 'object' && result[0] !== null) {
      return result.map(entry =>
        entry && typeof entry === 'object' && 'object' in entry ? entry.object ?? entry : entry
      )
    }

    return result
  }

  if (typeof result === 'object') {
    if (Array.isArray(result.items)) {
      return result.items
    }

    if (Array.isArray(result.data)) {
      return result.data
    }

    if (Array.isArray(result.rows)) {
      return result.rows
    }

    if ('object' in result && result.object) {
      return Array.isArray(result.object) ? result.object : [result.object]
    }
  }

  return []
}

export const EmbeddedListElementPreview: FC<EmbeddedListElementPreviewProps> = ({
  element,
  commandResults,
  selectedIds,
  onCheckboxChange,
  onContextMenuAction,
}) => {
  const { getListItemById } = useMobileListItems()
  const [executeCommand] = useExecuteCommandMutation()
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null)
  const [selectedItem, setSelectedItem] = useState<any>(null)
  const [loading, setLoading] = useState(false)
  const [listData, setListData] = useState<any[]>([])
  const [error, setError] = useState<string | null>(null)

  // Получаем конфигурацию списка
  const listItemConfig = useMemo(() => {
    if (element.embeddedListItemId) {
      return getListItemById(element.embeddedListItemId)
    } else if (element.embeddedListConfig) {
      return element.embeddedListConfig as MobileListItem
    }
    return null
  }, [element.embeddedListItemId, element.embeddedListConfig, getListItemById])

  const itemIdField = element.itemIdField || '_id'
  const showCheckboxes = element.showCheckboxes !== false

  // Загружаем данные если есть команды и нет данных в commandResults
  useEffect(() => {
    if (commandResults && Object.keys(commandResults).length > 0) {
      // Используем данные из commandResults, не загружаем заново
      setLoading(false)
      return
    }

    if (!listItemConfig?.getDataCommands?.length) {
      setListData([])
      setLoading(false)
      return
    }

    const loadData = async () => {
      setLoading(true)
      setError(null)

      try {
        const settled = await Promise.allSettled(
          listItemConfig.getDataCommands!.map(async command => {
            const params = getCommandRequestData(command.parameters ?? [], {})
            const body = getCommandRequestData(command.body ?? [], {})

            const response = await executeCommand({
              commandCode: command.name,
              objectCode: command.objectCode,
              method: command.httpMethod || 'POST',
              params,
              body,
            }).unwrap()

            return normalizeCommandRecords(response)
          })
        )

        const rejectedResult = settled.find(
          (result): result is PromiseRejectedResult => result.status === 'rejected'
        )

        if (rejectedResult) {
          setError(rejectedResult.reason?.message || 'Error loading data')
          setListData([])
        } else {
          const fulfilledResults = settled as PromiseFulfilledResult<any[]>[]
          const allRecords = fulfilledResults.flatMap(result => result.value)
          setListData(allRecords)
        }
      } catch (err: any) {
        setError(err?.message || 'Error loading data')
        setListData([])
      } finally {
        setLoading(false)
      }
    }

    loadData()
  }, [listItemConfig?.getDataCommands, executeCommand, commandResults])

  // Используем данные из commandResults если доступны, иначе из локального состояния
  const displayData = useMemo(() => {
    if (commandResults && Object.keys(commandResults).length > 0) {
      // Используем данные из commandResults
      const firstKey = Object.keys(commandResults)[0]
      return commandResults[firstKey] || []
    }
    return listData
  }, [commandResults, listData])

  const handleContextMenuOpen = (event: React.MouseEvent<HTMLElement>, item: any) => {
    event.stopPropagation()
    setAnchorEl(event.currentTarget)
    setSelectedItem(item)
  }

  const handleContextMenuClose = () => {
    setAnchorEl(null)
    setSelectedItem(null)
  }

  const handleActionClick = (action: ContextMenuAction) => {
    if (selectedItem) {
      onContextMenuAction(action, selectedItem)
    }
    handleContextMenuClose()
  }

  const getItemId = (item: any): string => {
    if (itemIdField === '_id') {
      return item._id || item.id || String(item)
    }
    return item[itemIdField] || item._id || item.id || String(item)
  }

  const isItemSelected = (item: any): boolean => {
    const id = getItemId(item)
    return selectedIds.includes(id)
  }

  const handleCheckboxChange = (item: any, checked: boolean) => {
    const id = getItemId(item)
    onCheckboxChange(id, checked)
  }

  if (!listItemConfig) {
    return (
      <Box sx={{ p: 2, textAlign: 'center' }}>
        <Typography variant="body2" color="text.secondary">
          No list configuration selected
        </Typography>
      </Box>
    )
  }

  if (loading) {
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', p: 3, gap: 2 }}>
        <CircularProgress size={24} />
        <Typography variant="body2" color="text.secondary">
          Loading data...
        </Typography>
      </Box>
    )
  }

  if (error) {
    return (
      <Box sx={{ p: 2 }}>
        <Typography variant="body2" color="error">
          {error}
        </Typography>
      </Box>
    )
  }

  if (displayData.length === 0) {
    return (
      <Box sx={{ p: 2, textAlign: 'center' }}>
        <Typography variant="body2" color="text.secondary">
          No data available
        </Typography>
      </Box>
    )
  }

  // Создаем модифицированную конфигурацию для MobileListItemPreview
  const previewListItem: MobileListItem = {
    ...listItemConfig,
    id: listItemConfig.id || 'preview',
    title: listItemConfig.title || 'Preview',
    itemType: listItemConfig.itemType || 'dictionary',
  }

  return (
    <Box>
      {displayData.map((item, index) => {
        const itemId = getItemId(item)
        const isSelected = isItemSelected(item)
        const hasContextMenu = element.contextMenuActions && element.contextMenuActions.length > 0

        return (
          <Box
            key={index}
            sx={{
              position: 'relative',
              mb: index < displayData.length - 1 ? 1.5 : 0,
            }}
          >
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'row',
                alignItems: 'stretch',
                gap: '8px',
              }}
            >
              {/* Checkbox */}
              {showCheckboxes && (
                <Box
                  sx={{
                    flexShrink: 0,
                    display: 'flex',
                    alignItems: 'center',
                    pt: '12px',
                    pb: '12px',
                  }}
                >
                  <Checkbox
                    checked={isSelected}
                    onChange={(e) => handleCheckboxChange(item, e.target.checked)}
                    size="small"
                    sx={{
                      padding: '4px',
                    }}
                  />
                </Box>
              )}

              {/* List Item Content - используем MobileListItemPreview для одного элемента */}
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <MobileListItemPreview
                  item={previewListItem}
                  data={[item]}
                  commandResultsByKey={commandResults}
                  commandResultsByObject={commandResults}
                  isLoading={false}
                  error={null}
                  onRefresh={() => {}}
                  onDataEdit={() => {}}
                />
              </Box>

              {/* Context Menu Button */}
              {hasContextMenu && (
                <Box
                  sx={{
                    flexShrink: 0,
                    display: 'flex',
                    alignItems: 'center',
                    pt: '12px',
                    pb: '12px',
                  }}
                >
                  <IconButton
                    size="small"
                    onClick={(e) => handleContextMenuOpen(e, item)}
                    sx={{
                      padding: '4px',
                    }}
                  >
                    <MoreVertIcon fontSize="small" />
                  </IconButton>
                </Box>
              )}
            </Box>
          </Box>
        )
      })}

      {/* Context Menu */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleContextMenuClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
      >
        {element.contextMenuActions?.map((action) => (
          <MenuItem key={action.internalId} onClick={() => handleActionClick(action)}>
            {action.icon && <Box component="span" sx={{ mr: 1 }}>{action.icon}</Box>}
            {action.title}
          </MenuItem>
        ))}
      </Menu>
    </Box>
  )
}
